import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { schedulerDefaultSettings } from 'app/scheduler/consts'
import { SchedulerSettings } from '@cbgms/api/modules/scheduler'
import { useSchedulerSettingsService } from 'app/settings/components/Scheduler/use-scheduler-settings-service'
import { GmsSchedulerPages, GmsSchedulerViewModes } from '@cbgms/api'

export interface ISchedulerContext {
  settings: SchedulerSettings
  setSettings: (settings: SchedulerSettings) => any
  settingsLoading: boolean
  saveLastVisitedView: (page: GmsSchedulerPages, newTab: GmsSchedulerViewModes) => Promise<void>
}

export const SchedulerContext = React.createContext<ISchedulerContext>({
  settings: { ...schedulerDefaultSettings },
  setSettings: () => {},
  settingsLoading: false,
  saveLastVisitedView: () => new Promise<void>(resolve => resolve())
})

export const SchedulerContextProvider: React.FC = ({ children }) => {
  const { loadSchedulerSettings, updateSchedulerSettings } = useSchedulerSettingsService()
  const [settings, setSettings] = React.useState<SchedulerSettings | undefined>()

  const {
    isLoading: settingsLoading,
    data: settingsData,
    refetch
  } = useQuery<SchedulerSettings, never>(['SchedulerContextProvider', 'loadSchedulerSettings'], async () => loadSchedulerSettings())

  React.useEffect(() => {
    setSettings(settingsData)
  }, [settingsData])

  const saveLastVisitedView = async (page: GmsSchedulerPages, newTab: GmsSchedulerViewModes) => {
    const currentSettings = { ...(settingsData as SchedulerSettings) }
    const newSettings = {
      ...currentSettings,
      LastVisitedViewByPage: { ...currentSettings?.LastVisitedViewByPage, [page]: newTab }
    }

    setSettings(newSettings as SchedulerSettings)

    // save last visited hash view
    try {
      await updateSchedulerSettings({
        LastVisitedViewByPage: newSettings.LastVisitedViewByPage
      })
    } catch (e) {
      // revert changes...
      refetch()
    }
  }

  const value = React.useMemo((): ISchedulerContext => {
    return {
      settings: settings as SchedulerSettings,
      setSettings,
      settingsLoading,
      saveLastVisitedView
    }
  }, [
    settings?.WorkDays,
    settings?.DaysToShowByView?.MainMechanics,
    settings?.DaysToShowByView?.MainWeek,
    settings?.DaysToShowByView?.MainWorkstations,
    settings?.DaysToShowByView?.Dashboard,
    settings?.DaysToShowByView?.PlanningMechanics,
    settings?.DaysToShowByView?.PlanningWorkstations,
    settings?.DaysToShowByView?.PlanningWeek,
    settings?.LastVisitedViewByPage[GmsSchedulerPages.MAIN],
    settings?.LastVisitedViewByPage[GmsSchedulerPages.DASHBOARD],
    settings?.LastVisitedViewByPage[GmsSchedulerPages.PLANNING],
    settingsLoading
  ])

  return <SchedulerContext.Provider value={value as ISchedulerContext}>{children}</SchedulerContext.Provider>
}

export const { Consumer: SchedulerContextConsumer } = SchedulerContext
